/// <amd-module name="Feed/Core/Components/FeedPicker" />
import * as React from "react";
import { connect } from "react-redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";

import { ObservableValue } from "VSS/Core/Observable";

import { Dropdown, IDropdownPivot, IWithPivotsChildProps, WithPivots } from "VSSUI/Dropdown";
import { IListBoxItem } from "VSSUI/ListBox";
import { AggregateItemProvider } from "VSSUI/Utilities/AggregateItemProvider";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { GroupedItemProvider } from "VSSUI/Utilities/GroupedItemProvider";
import { Location } from "VSSUI/Utilities/Position";

import * as PackageResources from "Feed/Common/Resources";
import * as FeedPickerSelector from "Feed/Core/Components/FeedPickerSelectors";
import * as LegacyActions from "Feed/Core/LegacyActions/LegacyFeedActions";
import { FeedHubActions } from "Feed/Core/Redux/FeedHub";
import { IFeedHubViewAwareState } from "Feed/Core/Redux/FeedHub/FeedHubContracts";
import { getFeedHubModules } from "Feed/Core/Redux/FeedHub/FeedHubModule";
import * as FeedHubSelector from "Feed/Core/Redux/FeedHub/FeedHubSelectors";

const Actions = {
    feedSelected: FeedHubActions.feedSelected
};

export interface IFeedPickerProps {
    activeFeedsGroupedItemProvider: GroupedItemProvider;
    deletedFeedsGroupedItemProvider: GroupedItemProvider;
    isFeedDeleted: boolean;
    selectedFeedId?: string;
}

const pivotId = {
    ACTIVE: "activeFeeds",
    DELETED: "deletedFeeds"
};

const groupIds = {
    PROJECT: PackageResources.FeedPicker_ProjectGroupId,
    COLLECTION: PackageResources.FeedPicker_CollectionGroupId
};

class UnconnectedFeedPicker extends React.Component<IFeedPickerProps & typeof Actions> {
    private activeFeedsProvider: GroupedItemProvider;
    private deletedFeedsProvider: GroupedItemProvider;
    private aggregatedProvider: AggregateItemProvider<IListBoxItem>;

    private selectedPivot: ObservableValue<string>;
    private pivots: IDropdownPivot[] = [];

    private groups = [{ id: groupIds.PROJECT }, { id: groupIds.COLLECTION }];

    private selection: DropdownSelection;

    public constructor(props: IFeedPickerProps & typeof Actions) {
        super(props);
        this.selection = new DropdownSelection();
        this.deletedFeedsProvider = new GroupedItemProvider([], this.groups, true);
        this.activeFeedsProvider = new GroupedItemProvider([], this.groups, true);
        this.aggregatedProvider = new AggregateItemProvider<IListBoxItem>();

        if (!this.props.selectedFeedId || !this.props.isFeedDeleted) {
            this.selectedPivot = new ObservableValue<string>(pivotId.ACTIVE);
        } else {
            this.selectedPivot = new ObservableValue<string>(pivotId.DELETED);
        }

        this.pivots = [
            {
                id: pivotId.ACTIVE,
                name: PackageResources.FeedPicker_ActiveFeedsPivotLabel,
                items: this.activeFeedsProvider,
                filterPlaceholderText: PackageResources.FeedPicker_Placeholder,
                filteredNoResultsText: PackageResources.FeedPicker_NoResultsText,
                noItemsText: PackageResources.FeedPicker_NoItemsText
            },
            {
                id: pivotId.DELETED,
                name: PackageResources.FeedPicker_DeletedFeedsPivotLabel,
                items: this.deletedFeedsProvider,
                filterPlaceholderText: PackageResources.FeedPicker_Placeholder,
                filteredNoResultsText: PackageResources.FeedPicker_NoResultsText,
                noItemsText: PackageResources.FeedPicker_NoItemsRecycleBinText
            }
        ];

        this.pivots.forEach(pivot => {
            this.aggregatedProvider.push(pivot.items);
        });
    }

    public render() {
        // Unselectable range lingers from previous state, possible web framework component bug
        this.selection.clearUnselectable();

        // Aggregate provider allows dropdown to be aware of items in one pivot
        // while showing items of another pivot. ex: select an active feed, then
        // browse to the deleted feeds pivot. Dropdown still needs to be aware
        // of active feeds so it can maintain the selected item
        let newAggregateProvider = new AggregateItemProvider<IListBoxItem>();
        this.pivots.forEach(pivot => {
            if (pivot.id == pivotId.ACTIVE) {
                pivot.items = this.props.activeFeedsGroupedItemProvider;
            } else {
                pivot.items = this.props.deletedFeedsGroupedItemProvider;
            }
            newAggregateProvider.push(pivot.items);
        });
        this.aggregatedProvider = newAggregateProvider;

        this.updateDropdownSelection(this.aggregatedProvider);

        return (
            <WithPivots
                calloutProps={{
                    title: PackageResources.FeedPicker_Title,
                    dropdownOrigin: {
                        horizontal: Location.start,
                        vertical: Location.start
                    },
                    anchorOrigin: {
                        horizontal: Location.start,
                        vertical: Location.end
                    }
                }}
                onPivotClicked={this.onPivotClicked}
                selectedPivot={this.selectedPivot}
                pivots={this.pivots}
            >
                {(props: IWithPivotsChildProps) => (
                    <Dropdown
                        className="feed-picker"
                        items={newAggregateProvider}
                        onSelect={(_event, item) => {
                            if (item.id) {
                                LegacyActions.UpdateLegacyFeedSelection.invoke(item.id);
                                // Updating redux store as well to prevent a race between store updates
                                this.props.feedSelected(item.id);
                            }
                        }}
                        {...props}
                        // Wide enough to display pivot titles side by side
                        width={250}
                        selection={this.selection}
                    />
                )}
            </WithPivots>
        );
    }

    private updateDropdownSelection(provider: AggregateItemProvider<IListBoxItem>) {
        if (this.props.selectedFeedId) {
            let index = 0;
            index = provider.value.findIndex(feed => {
                return feed.id && feed.id === this.props.selectedFeedId;
            });
            if (index !== -1) {
                this.selection.select(index);
            }
        }
    }

    onPivotClicked = (pivot: IDropdownPivot) => {
        this.selectedPivot.value = pivot.id;
    };
}

function mapStateToProps(state: IFeedHubViewAwareState): IFeedPickerProps {
    return {
        activeFeedsGroupedItemProvider: FeedPickerSelector.getActiveFeedsGroupedItemProvider(state),
        deletedFeedsGroupedItemProvider: FeedPickerSelector.getDeletedFeedsGroupedItemProvider(state),
        isFeedDeleted: FeedHubSelector.isSelectedFeedDeleted(state),
        selectedFeedId: FeedHubSelector.getSelectedFeedId(state)
    };
}

const ConnectedFeedPicker = connect(mapStateToProps, Actions)(UnconnectedFeedPicker);

export const FeedPicker = () => (
    <DynamicModuleLoader modules={getFeedHubModules()}>
        <ConnectedFeedPicker />
    </DynamicModuleLoader>
);
